import StatusBadge from 'components/admin/search/StatusBadge';
import FontIcon from 'components/icons/FontIcon';
import { EPD_FORMAT, epdFormatLookup } from 'components/v2/compiler/constants';
import { cellSelectEditor } from 'components/v2/compiler/grids/GridCellEditors';
import { ColumnProps } from 'primereact/column';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { EpdLinks } from 'routes/EpdRoutes';
import AuthService from 'services/AuthService';
import { EPDStatusDisplayName } from 'services/EpdClient';
import styled from 'styled-components';
import { AdminEpdSearchResult } from 'types/types';
import { formatDate } from 'util/utils';

type Result = Omit<ColumnProps, 'field'> & { field: keyof AdminEpdSearchResult };

export const useEpdSearchResultColumns = ({ agentsLookup, mergedAgentsLookup, updateAgentMutation }: any): Result[] => {
  const isAdmin = AuthService.isAdmin();

  return [
    {
      field: 'registrationNumber',
      header: 'Registration number',
      style: { minWidth: '7rem', whiteSpace: 'nowrap' },
      frozen: true,
      body: (row) => (
        <Link
          to={
            row.epdFormat === EPD_FORMAT.DYNAMIC_FORMS
              ? EpdLinks.dynamicForm(row.epdId)
              : row.isInGroup
              ? EpdLinks.showGroupEpd(row.epdId)
              : EpdLinks.editEpd(row.epdId)
          }
          style={{ cursor: 'pointer', textDecoration: 'none', color: 'inherit' }}
        >
          {row.registrationNumber}
          {row.isInGroup && <CopyGroupBadge>Copy group</CopyGroupBadge>}
        </Link>
      ),
    },
    {
      field: 'epdName',
      header: 'EPD name',
      style: { minWidth: '7rem', whiteSpace: 'nowrap' },
      body: (rowData) => <EllipsedCell title={rowData.epdName}>{rowData.epdName}</EllipsedCell>,
    },
    {
      field: 'epdOwner',
      header: 'EPD Owner',
      style: { minWidth: '7rem', whiteSpace: 'nowrap' },
      body: (rowData) => <EllipsedCell title={rowData.epdOwner}>{rowData.epdOwner}</EllipsedCell>,
    },
    {
      field: 'status',
      header: 'Status',
      style: { minWidth: '7rem', whiteSpace: 'nowrap' },
      body: (row, options) => <EpdStatusBadge status={row[options.field]} />,
    },
    {
      field: 'registrationDate',
      header: 'Registration date',
      style: { minWidth: '7rem', whiteSpace: 'nowrap' },
      body: (row, options) => formatDate(row[options.field]) || '-',
    },
    {
      field: 'agentId',
      hidden: !isAdmin,
      header: 'Agent',
      style: { minWidth: '7rem', whiteSpace: 'nowrap' },
      // Dynamic Forms documents have no agent assignment yet, so the cell is not editable for them.
      editor: (options) =>
        options.rowData.epdFormat === EPD_FORMAT.DYNAMIC_FORMS
          ? undefined
          : cellSelectEditor({
              options,
              selectOptions: agentsLookup,
              onValueChange: ({ rowData, newValue }) => {
                updateAgentMutation({
                  epdVersionId: rowData.epdVersionId,
                  agentId: newValue,
                });
              },
              placeholder: 'Unassigned',
            }),
      body: (row, options) => {
        if (row.epdFormat === EPD_FORMAT.DYNAMIC_FORMS) {
          return 'N/A';
        }

        const label = mergedAgentsLookup.labelByValue?.(row[options.field]) ?? 'Unassigned';

        return (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              minWidth: '4rem',
            }}
          >
            {label}
            <FontIcon icon="chevron-down" style={{ marginTop: '6px', fontSize: '16px' }} />
          </div>
        );
      },
    },
    {
      field: 'licensee',
      header: 'Licensee',
      style: { minWidth: '7rem', whiteSpace: 'nowrap' },
      body: (row, options) => row[options.field] || 'EPD International',
    },
    {
      field: 'epdFormat',
      header: 'EPD format',
      style: { minWidth: '7rem', whiteSpace: 'nowrap' },
      body: (row, options) => epdFormatLookup.labelByValue(row[options.field]),
    },
  ];
};

export const EllipsedCell = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 30ch;
  padding: 2px 0; /* Fix for clipping */
`;

export const EpdStatusBadge: React.FC<{ status: EPDStatusDisplayName }> = ({ status }) => {
  const { t } = useTranslation();
  const text = t(`epdWizard.epdState.status.${status}`);

  if (status === EPDStatusDisplayName.Reserved) {
    return <StatusBadge $variant="gray">{text}</StatusBadge>;
  }
  if (status === EPDStatusDisplayName.Published) {
    return <StatusBadge $variant="green">{text}</StatusBadge>;
  }
  if (
    status === EPDStatusDisplayName.SentForVerification ||
    status === EPDStatusDisplayName.ReadyToPublish ||
    status === EPDStatusDisplayName.LicenseeReview ||
    status === EPDStatusDisplayName.Updating ||
    status === EPDStatusDisplayName.SentForPublication
  ) {
    return <StatusBadge $variant="yellow">{text}</StatusBadge>;
  }
  if (
    status === EPDStatusDisplayName.DeclinedPublication ||
    status === EPDStatusDisplayName.Deregistered ||
    status === EPDStatusDisplayName.Depublished ||
    status === EPDStatusDisplayName.DeclinedVerification
  ) {
    return <StatusBadge $variant="red">{text}</StatusBadge>;
  }

  return <StatusBadge $variant="gray">{text}</StatusBadge>;
};

const CopyGroupBadge = styled.span`
  margin-left: 0.5rem;
  background-color: ${(props) => props.theme.colors.secondaryLightGreen30};
  color: ${(props) => props.theme.colors.primaryGreen};
  padding: 2px 6px;
  border-radius: 2px;
  ${(props) => props.theme.typography.headerCaptionMedium};
  font-size: 10px;
`;
